import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { useMetaPixel } from "@/hooks/useMetaPixel";
import { LandingNav } from "@/components/landing/LandingNav";
import { LandingFooter } from "@/components/landing/LandingFooter";
import { WhatsAppButton } from "@/components/landing/WhatsAppButton";
import { phCapture } from "@/lib/posthog-events";
import { Sparkles, ArrowRight, TrendingUp, Bot, Check, X, Users, BadgePercent, MessageSquare, MessageCircle, ShieldCheck, Rocket, PlayCircle } from "lucide-react";
import { HeroPhoneAnimation } from "@/components/landing/HeroPhoneAnimation";
import { LandingFaq } from "@/components/landing/LandingFaq";
import { RevenueCalculator } from "@/components/landing/RevenueCalculator";
import { Testimonials } from "@/components/landing/Testimonials";
import { AnimatedMenuPreview } from "@/components/landing/AnimatedMenuPreview";
import { AiWaiterChatDemo } from "@/components/landing/AiWaiterChatDemo";
import step1Img from "@/assets/onboarding-step1.png";
import step2Img from "@/assets/onboarding-step2.png";
import step3Img from "@/assets/onboarding-step3.png";
import { QRCodeSVG } from "qrcode.react";
import ctaBanner from "@/assets/cta-banner.png";

const steps = [
  {
    number: "1",
    title: "Envie seu cardápio",
    description: "Foto, PDF ou link. A IA extrai tudo em segundos: itens, preços, descrições e até fotos de cada prato.",
    image: step1Img,
  },
  {
    number: "2",
    title: "Pedidos caem no seu painel",
    description: "Cliente abre o link, monta o pedido e paga por PIX. Você confirma em 1 clique, sem comissão.",
    image: step2Img,
  },
  {
    number: "3",
    title: "A IA vende mais por você",
    description: "Garçom IA sugere pratos, insights mostram o que vende mais e o CRM avisa quando um cliente some.",
    image: step3Img,
  },
];

const heroRestaurant = new URL("../assets/hero-restaurant.jpg", import.meta.url).href;

const trackCta = (cta: string) => phCapture("cta_clicked", { cta, page: "landing" });

const Landing = () => {
  const { trackPageView } = useMetaPixel();

  useEffect(() => {
    trackPageView();
  }, [trackPageView]);

  return (
    <div className="min-h-screen bg-white">

      {/* Hero */}
      <div className="relative overflow-hidden bg-white">
        <LandingNav />

        {/* Fundo: círculo laranja com foto do restaurante + varredura diagonal */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          {/* Círculo grande (direita) */}
          <div className="absolute right-[-140px] top-[-60px] hidden h-[760px] w-[760px] overflow-hidden rounded-full bg-[#FF6A00] lg:block">
            <img
              src={heroRestaurant}
              alt=""
              aria-hidden="true"
              width={1024}
              height={1024}
              className="absolute inset-0 h-full w-full object-cover opacity-90"
            />
            <div className="absolute inset-0 bg-[#FF6A00]/45 mix-blend-multiply" />
            {/* metade esquerda do círculo em laranja sólido */}
            <div className="absolute inset-y-0 left-0 w-1/2 bg-gradient-to-r from-[#FF6A00] via-[#FF6A00] to-transparent" />
          </div>

          {/* Versão reduzida em telas médias */}
          <div className="absolute right-[-160px] top-0 hidden h-[520px] w-[520px] overflow-hidden rounded-full bg-[#FF6A00] md:block lg:hidden" />

          {/* Varredura diagonal na base ligando o círculo ao canto inferior esquerdo */}
          <div
            className="absolute -left-[5%] bottom-0 h-[300px] w-[115%] bg-gradient-to-r from-[#FF8A2B] to-[#FF5A00]"
            style={{ clipPath: "polygon(0 78%, 100% 6%, 100% 100%, 0% 100%)" }}
          />
          <div
            className="absolute -left-[5%] bottom-0 h-[340px] w-[115%] bg-[#FF6A00]/15"
            style={{ clipPath: "polygon(0 84%, 100% 14%, 100% 100%, 0% 100%)" }}
          />
        </div>




        <section className="container relative z-10 pb-16 pt-10 md:pb-24 md:pt-16">
          <div className="grid gap-12 md:grid-cols-2 md:items-center md:gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <p className="flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#FF6A00] md:justify-start">
                <Rocket className="h-4 w-4" />
                Seu restaurante online
              </p>

              <h1 className="mt-5 text-center text-4xl font-bold leading-[1.08] tracking-tight md:text-left md:text-5xl lg:text-6xl">
                Tire uma foto do seu cardápio.
                <span className="mt-2 block text-[#FF6A00]">
                  Em minutos, seu sistema está no ar.
                </span>
              </h1>

              <p className="mt-6 text-center text-lg leading-relaxed text-muted-foreground md:text-left">
                Cardápio digital, pedidos no WhatsApp e PIX integrado.
                <br className="hidden sm:block" />
                Você vende direto para o cliente e{" "}
                <strong className="font-semibold text-foreground">não paga comissão</strong>.
              </p>

              <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row md:justify-start">
                <Button size="lg" asChild className="h-14 rounded-full px-8 text-base font-semibold bg-[#FF6A00] hover:bg-[#e55f00] text-white shadow-lg shadow-[#FF6A00]/30">
                  <Link to="/entrar" data-attr="hero_criar_cardapio" onClick={() => trackCta("hero_criar_cardapio")}>
                    Montar meu sistema <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  asChild
                  className="h-14 rounded-full border-[#FF6A00]/40 px-7 text-base font-semibold text-foreground hover:bg-[#FF6A00]/5"
                >
                  <Link to="/menu/demo" data-attr="hero_ver_demo" onClick={() => trackCta("hero_ver_demo")}>
                    <PlayCircle className="mr-2 h-5 w-5 text-[#FF6A00]" />
                    Ver como funciona
                  </Link>
                </Button>
              </div>

              {/* Social proof */}
              <div className="mt-10 inline-flex items-center gap-4 rounded-2xl bg-white px-5 py-3 shadow-[0_10px_40px_-12px_rgba(0,0,0,0.18)]">
                <div className="flex -space-x-3">
                  {["ana", "marcos", "carla", "rafael", "ana"].map((name, i) => (
                    <img
                      key={i}
                      src={`/avatars/${name}.svg`}
                      alt=""
                      aria-hidden="true"
                      loading="lazy"
                      className="h-10 w-10 rounded-full border-2 border-white bg-zinc-100 object-cover"
                    />
                  ))}
                </div>
                <span className="flex h-10 items-center rounded-full bg-[#FF6A00]/10 px-3 text-sm font-bold text-[#FF6A00]">
                  100+
                </span>
                <div className="text-sm leading-tight">
                  <p className="font-semibold">usuários</p>
                  <p className="text-muted-foreground">já vendem mais</p>
                </div>
              </div>
            </motion.div>

            {/* Phone */}
            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative mx-auto w-full max-w-md md:translate-y-6"
              style={{ perspective: "1400px" }}
            >
              <div
                className="relative mx-auto"
                style={{
                  width: "min(100%, 340px)",
                  transform: "rotateY(-9deg) rotate(-5deg)",
                  transformStyle: "preserve-3d",
                }}
              >
                <div
                  className="pointer-events-none absolute left-1/2 -translate-x-1/2 rounded-[50%]"
                  style={{ bottom: "-32px", width: "80%", height: "52px", background: "radial-gradient(ellipse, rgba(0,0,0,0.35) 0%, transparent 70%)", filter: "blur(10px)" }}
                />
                <div
                  className="relative rounded-[3rem] border-[12px] border-zinc-900 bg-zinc-900 shadow-[0_60px_120px_-25px_rgba(0,0,0,0.55)]"
                  style={{ aspectRatio: "9/19" }}
                >
                  <HeroPhoneAnimation />
                </div>
              </div>


              {/* Floating "Novo pedido" card */}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="absolute -right-2 top-[38%] z-20 w-[248px] md:-right-16 lg:-right-24 rounded-2xl bg-white p-4 shadow-[0_20px_50px_-15px_rgba(0,0,0,0.3)]"
              >
                <div className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#25D366]">
                    <MessageCircle className="h-5 w-5 text-white" fill="white" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <p className="text-sm font-bold">Novo pedido</p>
                      <span className="text-[11px] text-muted-foreground">agora</span>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">2x Smash Burger Duplo</p>
                    <p className="text-xs text-muted-foreground">1x Coca Cola 350ml</p>
                    <p className="mt-2 text-lg font-bold text-[#FF6A00]">R$ 79,70</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>
      </div>


      {/* ① Como funciona — BRANCO */}
      <section className="relative -mt-8 rounded-t-[2.5rem] bg-white py-16 md:py-24">
        <div className="container">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-semibold uppercase tracking-wider text-[#FF6A00]">
              Do zero ao primeiro pedido
            </p>
            <h2 className="mt-2 text-3xl font-bold tracking-tight md:text-4xl">
              Pronto em 3 passos
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              Envie seu cardápio. O resto a IA resolve.
            </p>
          </div>
          <div className="mt-12 grid gap-4 md:mt-14 md:grid-cols-3 md:gap-6 lg:gap-8">
            {steps.map((step, i) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15, duration: 0.5 }}
                className="group relative overflow-hidden rounded-3xl bg-zinc-50 text-left shadow-sm transition-shadow hover:shadow-md"
              >
                <div className="flex items-center justify-center px-4 pt-4 md:px-5 md:pt-5">
                  <img src={step.image} alt={step.title} className="h-32 w-auto object-contain md:h-40 lg:h-44" loading="lazy" />
                </div>
                <div className="flex items-start gap-3 p-5 md:p-6">
                  <span className="text-lg font-semibold text-[#FF6A00] md:text-xl">{step.number}</span>
                  <div>
                    <h3 className="text-lg font-semibold leading-tight md:text-xl">{step.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground md:text-[15px]">{step.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Button size="lg" asChild className="h-13 px-8 bg-[#FF6A00] hover:bg-[#e55f00] text-white">
              <Link to="/entrar" data-attr="howitworks_cta" onClick={() => trackCta("howitworks_cta")}>
                Montar meu sistema <ArrowRight className="ml-1.5 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* ② Painel + Clientes — LARANJA */}
      <section className="relative -mt-8 rounded-t-[2.5rem] bg-[#FF6A00] py-16 md:py-24">
        <div className="container">
          <div className="grid gap-12 md:grid-cols-2 md:items-center md:gap-16">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="rounded-2xl bg-white p-5 shadow-xl space-y-3"
            >
              <div className="flex items-center justify-between border-b pb-3">
                <p className="text-sm font-semibold flex items-center gap-2 text-zinc-900">
                  <Users className="h-4 w-4 text-[#FF6A00]" /> Clientes
                </p>
                <span className="text-xs bg-[#FF6A00]/10 text-[#FF6A00] px-2.5 py-0.5 rounded-full font-medium">24 clientes</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {[
                  { label: "Todos", count: 24, active: true },
                  { label: "Fiéis", count: 8, active: false },
                  { label: "Inativos", count: 5, active: false },
                  { label: "Sumidos", count: 3, active: false },
                ].map((seg) => (
                  <span
                    key={seg.label}
                    className={`rounded-lg px-2.5 py-1 text-xs font-medium ${seg.active ? "bg-zinc-100 shadow-sm text-zinc-900 border" : "text-zinc-500"}`}
                  >
                    {seg.label} <span className="opacity-60">{seg.count}</span>
                  </span>
                ))}
              </div>
              <div className="space-y-2">
                {[
                  { name: "Maria Silva", detail: "12 pedidos · há 2 dias", tag: "Fiel", tagClass: "bg-emerald-500/10 text-emerald-700" },
                  { name: "João Santos", detail: "3 pedidos · há 42 dias", tag: "Sumido", tagClass: "bg-red-500/10 text-red-600" },
                  { name: "Ana Costa", detail: "5 pedidos · há 18 dias", tag: "Inativo", tagClass: "bg-amber-500/10 text-amber-700" },
                ].map((c) => (
                  <div key={c.name} className="flex items-center justify-between rounded-lg bg-zinc-50 px-3 py-2.5">
                    <div>
                      <p className="text-sm font-medium leading-none text-zinc-900">{c.name}</p>
                      <p className="text-xs text-zinc-500 mt-0.5">{c.detail}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.tagClass}`}>{c.tag}</span>
                      <MessageSquare className="h-3.5 w-3.5 text-zinc-400" />
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-start gap-2 rounded-lg border border-dashed border-[#FF6A00]/30 bg-[#FF6A00]/5 px-3 py-2.5 text-xs font-medium text-[#FF6A00]">
                <Sparkles className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                "3 clientes fiéis não pedem há mais de 30 dias. Mande uma oferta pra trazer de volta."
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/20 px-4 py-1.5 text-sm font-medium text-white">
                <TrendingUp className="h-4 w-4" /> Mais que um cardápio
              </div>
              <h2 className="text-3xl font-bold tracking-tight text-white md:text-4xl">
                Seu painel vende por você, e traz cliente de volta
              </h2>
              <p className="mt-4 text-lg text-white/80 leading-relaxed">
                Cada pedido vira dado. A IA mostra o que vende, quem é fiel e quem está sumindo, e te dá o caminho direto pra recuperar.
              </p>
              <ul className="mt-6 space-y-3">
                {[
                  "Board de pedidos em tempo real com notificação push",
                  "Segmentação automática: Fiéis, Inativos e Sumidos",
                  "Mensagem no WhatsApp com 1 clique pra reativar quem sumiu",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-white/80">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/25">
                      <Check className="h-3 w-3 text-white" strokeWidth={3} />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
              <Button size="lg" className="mt-8 w-full sm:w-auto h-14 bg-white text-[#FF6A00] hover:bg-white/90 font-semibold" asChild>
                <Link to="/entrar" data-attr="painel_cta" onClick={() => trackCta("painel_cta")}>
                  Quero esse painel <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ③ Garçom IA — BRANCO */}
      <section className="relative -mt-8 rounded-t-[2.5rem] bg-white py-16 md:py-24">
        <div className="container">
          <div className="grid gap-12 md:grid-cols-2 md:items-center md:gap-16">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border bg-background px-4 py-1.5 text-sm font-medium text-[#FF6A00]">
                <Bot className="h-4 w-4" /> Exclusivo MenuzAI
              </div>
              <h2 className="text-3xl font-bold tracking-tight md:text-4xl">
                Um garçom com IA que aumenta seu ticket médio, 24h por dia
              </h2>
              <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
                Quando o cliente adiciona um lanche, o Garçom IA sugere uma bebida ou acompanhamento que combina e leva direto pro pedido.
              </p>
              <ul className="mt-6 space-y-3">
                {[
                  "Sugere acompanhamento automaticamente a cada item adicionado",
                  "Adiciona ao carrinho com 1 toque, sem sair da conversa",
                  "Tira dúvidas e leva direto pro Finalizar Pedido",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#FF6A00]/10">
                      <Check className="h-3 w-3 text-[#FF6A00]" strokeWidth={3} />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
              <Button variant="hero" size="lg" className="mt-8 w-full sm:w-auto" asChild>
                <Link to="/entrar" data-attr="garcom_ia_cta" onClick={() => trackCta("garcom_ia_cta")}>
                  Ativar Garçom IA <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <AiWaiterChatDemo />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ③b Atendimento Inteligente — BRANCO (mesma faixa da seção Garçom IA) */}
      <section className="relative bg-white pb-16 pt-2 md:pb-24">
        <div className="container">
          <div className="grid gap-12 md:grid-cols-2 md:items-center md:gap-16">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border bg-background px-4 py-1.5 text-sm font-medium text-[#FF6A00]">
                <MessageCircle className="h-4 w-4" /> A partir do plano Pedidos
              </div>
              <h2 className="text-3xl font-bold tracking-tight md:text-4xl">
                Atendimento Inteligente que conhece seu cardápio de cor
              </h2>
              <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
                Nada de robô genérico de WhatsApp. Ele conhece seus produtos, adicionais, disponibilidade, horário e preço — e ajuda seu cliente a fechar o pedido sozinho.
              </p>
              <ul className="mt-6 space-y-3">
                {[
                  "Enquanto sua equipe atende o salão, ele responde os clientes do delivery",
                  "Seu restaurante continua atendendo pedidos pelo WhatsApp mesmo nos horários de maior movimento",
                  "Tira dúvidas sobre o cardápio e conduz o cliente até o pedido, direto no seu número",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#FF6A00]/10">
                      <Check className="h-3 w-3 text-[#FF6A00]" strokeWidth={3} />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
              <div className="mt-4 flex items-center gap-1.5 text-xs text-muted-foreground">
                <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
                API Oficial da Meta — sem número paralelo, sem risco de bloqueio
              </div>
              <Button variant="hero" size="lg" className="mt-8 w-full sm:w-auto" asChild>
                <Link to="/entrar" data-attr="atendimento_ia_cta" onClick={() => trackCta("atendimento_ia_cta")}>
                  Conhecer o Atendimento Inteligente <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <div className="mx-auto max-w-sm rounded-3xl bg-white p-5 shadow-xl ring-1 ring-zinc-100">
                <div className="mb-4 flex items-center justify-between border-b pb-3">
                  <p className="flex items-center gap-2 text-sm font-semibold text-zinc-900">
                    <MessageCircle className="h-4 w-4 text-emerald-500" /> Atendimento Inteligente
                  </p>
                  <span className="flex items-center gap-1 rounded-full bg-emerald-500/10 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> Online agora
                  </span>
                </div>
                <div className="space-y-2.5">
                  <div className="ml-auto max-w-[80%] rounded-2xl rounded-tr-sm bg-emerald-500 px-3.5 py-2 text-sm text-white">
                    Boa noite! Vocês entregam até que horas hoje?
                  </div>
                  <div className="mr-auto max-w-[85%] rounded-2xl rounded-tl-sm bg-zinc-100 px-3.5 py-2 text-sm text-zinc-800">
                    Hoje entregamos até 23h30! Já vi seu bairro aqui, quer que eu te mostre o cardápio?
                  </div>
                  <div className="ml-auto max-w-[80%] rounded-2xl rounded-tr-sm bg-emerald-500 px-3.5 py-2 text-sm text-white">
                    Tem opção vegetariana?
                  </div>
                  <div className="mr-auto max-w-[85%] rounded-2xl rounded-tl-sm bg-zinc-100 px-3.5 py-2 text-sm text-zinc-800">
                    Temos sim 🌱 A Pizza Margherita e o Açaí Premium são os mais pedidos. Adiciono a Margherita pro seu pedido?
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ④ PIX — LARANJA */}
      <section className="relative -mt-8 rounded-t-[2.5rem] bg-[#FF6A00] py-16 md:py-24">
        <div className="container">
          <div className="grid gap-12 md:grid-cols-2 md:items-center md:gap-16">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-2 md:order-1"
            >
              <div className="mx-auto max-w-sm rounded-3xl bg-white p-6 shadow-xl">
                <div className="flex items-center justify-between border-b pb-3 mb-4">
                  <p className="text-sm font-semibold flex items-center gap-2 text-zinc-900">
                    <BadgePercent className="h-4 w-4 text-emerald-500" /> Pagamento PIX
                  </p>
                  <span className="text-xs bg-emerald-500/10 text-emerald-700 px-2.5 py-0.5 rounded-full font-medium">Pedido #243</span>
                </div>
                <div className="mx-auto mb-4 flex items-center justify-center rounded-xl bg-white p-3 ring-1 ring-zinc-200">
                  <QRCodeSVG value="https://menuzai.com" size={136} bgColor="#ffffff" fgColor="#18181b" level="M" />
                </div>
                <p className="text-center text-2xl font-extrabold text-zinc-900 mb-1">R$ 87,00</p>
                <p className="text-center text-xs text-zinc-500 mb-4">PIX Copia e Cola disponível</p>
                <div className="flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2.5">
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-500 shrink-0">
                    <Check className="h-4 w-4 text-white" strokeWidth={3} />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-emerald-800 leading-none">Comprovante recebido</p>
                    <p className="text-[11px] text-emerald-700 mt-0.5">Confirmar pedido com 1 clique</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="order-1 md:order-2"
            >
              <h2 className="text-3xl font-bold tracking-tight text-white md:text-4xl">
                Receba <span className="text-white/90">PIX direto no checkout</span>, sem comissão
              </h2>
              <p className="mt-4 text-lg text-white/80 leading-relaxed">
                O cliente paga por PIX e o dinheiro cai 100% no seu banco. Você confirma o pedido em 1 clique.
              </p>
              <ul className="mt-6 space-y-3">
                {[
                  "QR Code do seu PIX gerado automaticamente no checkout",
                  "Dinheiro cai direto na sua conta, sem taxa e sem atravessador",
                  "Confirma o pedido com 1 clique no dashboard",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-white/80">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/25">
                      <Check className="h-3 w-3 text-white" strokeWidth={3} />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
              <Button size="lg" className="mt-8 w-full sm:w-auto h-14 bg-white text-[#FF6A00] hover:bg-white/90 font-semibold" asChild>
                <Link to="/entrar" data-attr="pix_cta" onClick={() => trackCta("pix_cta")}>
                  Receber PIX sem comissão <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ⑤ Comparação — BRANCO */}
      <section className="relative -mt-8 rounded-t-[2.5rem] bg-white py-14 md:py-20">
        <div className="container">
          <div className="mx-auto max-w-2xl text-center mb-10">
            <p className="text-xs font-semibold uppercase tracking-wider text-[#FF6A00]">
              Por que MenuzAI
            </p>
            <h2 className="mt-2 text-3xl font-bold tracking-tight md:text-4xl">
              Outros sistemas ficam entre você e seu cliente.{" "}
              <span className="text-muted-foreground">O MenuzAI não.</span>
            </h2>
          </div>

          <div className="mx-auto max-w-3xl overflow-hidden rounded-2xl bg-white shadow-xl border">
            <div className="grid grid-cols-[1fr_72px_72px] sm:grid-cols-[1fr_168px_168px] border-b bg-zinc-50">
              <div className="p-2 sm:p-4" />
              <div className="p-2 sm:p-4 text-center border-l bg-[#FF6A00]/5">
                <span className="text-xs sm:text-sm font-bold text-[#FF6A00]">MenuzAI</span>
              </div>
              <div className="p-2 sm:p-4 text-center border-l">
                <span className="text-xs sm:text-sm font-medium text-muted-foreground">Apps de delivery</span>
              </div>
            </div>
            {[
              { label: "Comissão",             menuzai: "0%",        sem: "até 27%" },
              { label: "PIX na sua conta",      menuzai: "Na hora",   sem: "14 dias" },
              { label: "Atualizar cardápio",    menuzai: "Segundos",  sem: "Aprovação" },
              { label: "Dados dos clientes",    menuzai: "100% seus", sem: "Deles" },
              { label: "Garçom IA 24h",         menuzai: true,        sem: false },
              { label: "CRM + Reativação",      menuzai: true,        sem: false },
              { label: "Cancela quando quiser", menuzai: true,        sem: false },
            ].map((row, i) => (
              <div
                key={row.label}
                className={`grid grid-cols-[1fr_72px_72px] sm:grid-cols-[1fr_168px_168px] border-b last:border-b-0 ${i % 2 === 0 ? "bg-white" : "bg-zinc-50/50"}`}
              >
                <div className="p-2 sm:p-4 text-xs sm:text-sm font-medium flex items-center">{row.label}</div>
                <div className="p-2 sm:p-4 border-l bg-[#FF6A00]/5 flex items-center justify-center">
                  {typeof row.menuzai === "boolean" ? (
                    row.menuzai
                      ? <Check className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600" strokeWidth={3} />
                      : <X className="h-4 w-4 sm:h-5 sm:w-5 text-zinc-300" />
                  ) : (
                    <span className="text-xs sm:text-sm font-bold text-[#FF6A00]">{row.menuzai}</span>
                  )}
                </div>
                <div className="p-2 sm:p-4 border-l flex items-center justify-center">
                  {typeof row.sem === "boolean" ? (
                    row.sem
                      ? <Check className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600" strokeWidth={3} />
                      : <X className="h-4 w-4 sm:h-5 sm:w-5 text-zinc-400" />
                  ) : (
                    <span className="text-xs sm:text-sm text-muted-foreground">{row.sem}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ⑥ Testimonials — LARANJA */}
      <div className="relative -mt-8 rounded-t-[2.5rem] overflow-hidden bg-[#FF6A00]">
        <Testimonials />
      </div>

      {/* ⑦ RevenueCalculator — BRANCO */}
      <div className="relative -mt-8 rounded-t-[2.5rem] overflow-hidden bg-white">
        <RevenueCalculator />
      </div>

      {/* ⑧ AnimatedMenuPreview — LARANJA */}
      <section className="relative -mt-8 rounded-t-[2.5rem] bg-[#FF6A00] py-16 md:py-24">
        <div className="container">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-white md:text-4xl">
              Veja um cardápio de verdade, interativo
            </h2>
            <p className="mt-4 text-lg text-white/80">
              Esse é um cardápio real do MenuzAI. Abra, navegue, adicione ao carrinho, exatamente como seu cliente vai fazer.
            </p>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mx-auto mt-10 max-w-3xl"
          >
            <AnimatedMenuPreview />
            <div className="mt-6 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <Button size="lg" className="w-full sm:w-auto h-14 bg-white text-[#FF6A00] hover:bg-white/90 font-semibold" asChild>
                <Link to="/demo" target="_blank" rel="noopener noreferrer" data-attr="example_open_demo" onClick={() => trackCta("example_open_demo")}>
                  Abrir o cardápio interativo <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
              <span className="text-sm text-white/60">funciona no celular e no computador</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ⑨ FAQ — BRANCO */}
      <div className="relative -mt-8 rounded-t-[2.5rem] overflow-hidden bg-white">
        <LandingFaq />
      </div>

      {/* Final CTA */}
      <section className="container py-10 md:py-20">
        <div className="relative overflow-hidden rounded-2xl">
          <img src={ctaBanner} alt="" aria-hidden className="pointer-events-none absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-black/20" />
          <div className="relative z-10 px-6 py-16 text-center sm:px-12 md:px-16 lg:px-48 md:py-24">
            <motion.h2
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="text-3xl font-bold tracking-tight text-white md:text-4xl lg:text-5xl"
            >
              Seu restaurante pode estar<br className="hidden sm:block" /> recebendo pedidos hoje à noite.
            </motion.h2>
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              viewport={{ once: true }}
              className="mt-8"
            >
              <Button variant="secondary" size="lg" className="h-14 px-10 text-base font-semibold shadow-lg" asChild>
                <Link to="/entrar" data-attr="bottom_criar_cardapio" onClick={() => trackCta("bottom_criar_cardapio")}>
                  Começar agora <ArrowRight className="ml-1.5 h-5 w-5" />
                </Link>
              </Button>
              <p className="mt-4 text-sm text-white/75">
                Cardápio no ar em minutos · cancele quando quiser
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <LandingFooter />
      <WhatsAppButton />
    </div>
  );
};

export default Landing;
